/**
 * Lista de transações ecommerce (rota /ecommerce).
 * @module EcommerceListPage
 */

import { memo, type ReactNode } from "react";
import { Alert, Stack } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import type { Organization, Patient } from "fhir/r5";
import { LargeContentBox } from "@/components/common";
import { PageHeader } from "@/components/common/PageHeader";
import type { UseEcommerceWhatsAppEndpointResult } from "src-next/modules/ecommerce";
import { EcommercePatientsListSection } from "./EcommercePatientsListSection";
import EcommercePatientsListSkeleton from "../components/EcommercePatientsListSkeleton";

interface EcommerceListPageProps {
  organization: Organization;
  whatsAppStatusChip: ReactNode;
  whatsAppChannel: UseEcommerceWhatsAppEndpointResult;
  listRevision: number;
  onOpenManage: (
    patient: Patient,
    transactionId?: string,
    listSearch?: string,
  ) => void;
  onSendWhatsApp: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
}

export const EcommerceListPage = memo(function EcommerceListPage({
  organization,
  whatsAppStatusChip,
  whatsAppChannel,
  listRevision,
  onOpenManage,
  onSendWhatsApp,
}: EcommerceListPageProps) {
  return (
    <LargeContentBox>
      <Stack spacing={3} sx={{ width: "100%" }}>
        <PageHeader
          title="Ecommerce"
          icon={ShoppingCartOutlinedIcon}
          description="Verifique documentos, libere produtos para compra e acompanhe pedidos dos pacientes com consentimento."
          action={whatsAppStatusChip}
        />

        <EcommercePatientsListSection
          organization={organization}
          onOpenPage={onOpenManage}
          listRevision={listRevision}
          whatsAppChannel={whatsAppChannel}
          onSendWhatsApp={onSendWhatsApp}
        />
      </Stack>
    </LargeContentBox>
  );
});

export const EcommerceListPageSkeleton = memo(
  function EcommerceListPageSkeleton({
    action,
  }: {
    action?: ReactNode;
  }) {
    return (
      <LargeContentBox>
        <Stack spacing={3} sx={{ width: "100%" }}>
          <PageHeader
            title="Ecommerce"
            icon={ShoppingCartOutlinedIcon}
            description="Verifique documentos, libere produtos para compra e acompanhe pedidos dos pacientes com consentimento."
            action={action}
          />
          <EcommercePatientsListSkeleton />
        </Stack>
      </LargeContentBox>
    );
  },
);

export const EcommerceOrganizationUnavailable = memo(
  function EcommerceOrganizationUnavailable() {
    return (
      <LargeContentBox>
        <Alert severity="info">
          A organização selecionada não é do tipo ecommerce.
        </Alert>
      </LargeContentBox>
    );
  },
);
